import { Component, Input, Output, EventEmitter } from '@angular/core';
import { AgentCategory } from '../../models';

/**
 * Filter Tabs Component
 * 
 * Displays horizontal tabs for filtering agents by category.
 * Includes an "All" tab with total count and dynamic category tabs.
 * 
 * Features:
 * - "All (count)" tab always first
 * - Dynamic category tabs from input array
 * - Active state highlighting
 * - Keyboard accessibility
 * 
 * Usage:
 * <app-filter-tabs
 *   [categories]="categoryList"
 *   [activeCategory]="'all'"
 *   [totalCount]="6"
 *   (categoryChanged)="onCategoryChange($event)">
 * </app-filter-tabs>
 */
@Component({
  selector: 'app-filter-tabs',
  templateUrl: './filter-tabs.component.html',
  styleUrls: ['./filter-tabs.component.scss']
})
export class FilterTabsComponent {
  /**
   * Array of categories to display as tabs
   */
  @Input() categories: AgentCategory[] = [];

  /**
   * Currently active category ID ('all' for all agents)
   */
  @Input() activeCategory: string = 'all';

  /**
   * Total count of all agents (shown in "All" tab)
   */
  @Input() totalCount: number = 0;

  /**
   * Emits when a category tab is clicked
   */
  @Output() categoryChanged = new EventEmitter<string>();

  /**
   * Handle tab click
   * 
   * @param categoryId - ID of the clicked category
   */
  onTabClick(categoryId: string): void {
    if (categoryId !== this.activeCategory) {
      this.categoryChanged.emit(categoryId);
    }
  }

  /**
   * Handle keyboard navigation
   * 
   * @param event - Keyboard event
   * @param categoryId - ID of the focused category
   */
  onKeyDown(event: KeyboardEvent, categoryId: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onTabClick(categoryId);
    }
  }

  /**
   * Check if a category tab is active
   * 
   * @param categoryId - Category ID to check
   * @returns boolean - True if category is active
   */
  isActive(categoryId: string): boolean {
    return this.activeCategory === categoryId;
  }

  /**
   * Track by function for ngFor performance
   * 
   * @param index - Item index
   * @param category - Category item
   * @returns string - Unique identifier
   */
  trackByCategory(index: number, category: AgentCategory): string {
    return category.id;
  }
}
